import { api } from './api'
import { Icon } from './icon'
import util from './util'

class NewBusiness {
  constructor(elem) {
    this.$fieldset = elem
    this.$comboBoxNext = elem.querySelector('.ts-control')
    this.$comboBox = elem.querySelector('[data-folios-select]')
    this.$container = elem.querySelector('[data-nested-records="submissions"]')
    this.$addLink = elem.querySelector('[data-nested-records-control="add"]')
    this.$records = Array.from(elem.querySelectorAll('.new-business-record'))
  }

  init() {
    if (!this.$comboBox) return

    this.hideAddLink()

    if (this.$comboBox) {
      this.$comboBox.addEventListener('change', this.handleSelection.bind(this))
    }

    this.disablePopulatedRows()
    this.setStageForDeletionListener()
    this.hoverMenuHandler()
  }

  setStageForDeletionListener() {
    this.$container.addEventListener('cocoon:before-remove', this.toggleDeletion.bind(this))
  }

  toggleDeletion(cocoonEvent) {
    const [contentNode, originalEvent] = cocoonEvent.detail

    if (originalEvent.target.closest('.new-business-record[data-persisted="false"]')) {
      this.deleteSubmission(cocoonEvent)
      return
    }

    cocoonEvent.preventDefault()

    const target = originalEvent.target
    const targetText = target.parentElement.querySelector('.delete-text').innerHTML

    setTimeout(() => {
      if (targetText === 'Delete') {
        target.parentElement.querySelector('.delete-text').innerHTML = 'Undo Delete'
        target.parentElement.querySelector('.undo').classList.remove('hidden')
        target.parentElement.querySelector('.trash').classList.add('hidden')
      } else {
        target.parentElement.querySelector('.delete-text').innerHTML = 'Delete'
        target.parentElement.querySelector('.undo').classList.add('hidden')
        target.parentElement.querySelector('.trash').classList.remove('hidden')
      }
    }, 250)

    const hiddenInputs = contentNode.querySelectorAll('input[type="hidden"]')

    hiddenInputs.forEach(input => {
      if (input.name.includes('_destroy')) {
        if (input?.value === '1') {
          this.undoDeletion(input)
        } else {
          this.stageForDeletion(input)
        }
      }
    })
  }

  undoDeletion(input) {
    input.value = 'false'

    this.handleDeletedChip(input, 'delete')
    this.handleCardOverlay(input, 'delete')
    this.updateIcon(input, 'undo')
  }

  stageForDeletion(input) {
    input.value = '1'

    this.handleDeletedChip(input)
    this.handleCardOverlay(input)
    this.updateIcon(input)
  }

  updateIcon(input, value = 'stage') {
    const trashIcon = input.closest('.new-business-record')?.querySelector('.record-trash-icon')
    const undoIcon = input.closest('.new-business-record')?.querySelector('.record-undo-icon')
    const anchor = input.closest('li')?.querySelector('a')
    const anchorTooltip = anchor.dataset.tooltip
    const hoverMenu = input.closest('.new-business-record').querySelector('.trash-icon-container')

    if (!trashIcon || !undoIcon) {
      return
    }

    if (anchorTooltip === 'Delete Submission') {
      hoverMenu.classList.toggle('hover-menu')
      window.app.tooltipManager.create(anchor, null, null, 'Undo Deletion')
      anchor.dataset.tooltip = 'Undo Deletion'
    } else {
      hoverMenu.classList.toggle('hover-menu')
      window.app.tooltipManager.create(anchor, null, null, 'Delete Submission')
      anchor.dataset.tooltip = 'Delete Submission'
    }

    undoIcon.classList.toggle('hidden')
    trashIcon.classList.toggle('hidden')
  }

  handleCardOverlay(input, value = 'add') {
    const listItems = Array.from(
      input
        .closest('.new-business-record')
        .querySelector('.record-header-container')
        .querySelectorAll('.record-header-list-item'),
    )
    const recordBody = input.closest('.new-business-record').querySelector('.record-body')

    if (value === 'delete') {
      this.handleRecordClasses('remove', listItems, recordBody)
      return
    }

    this.handleRecordClasses('add', listItems, recordBody)
  }

  handleRecordClasses(val, listItems, recordBody) {
    recordBody.classList[val]('bg-gray-50', 'opacity-50')

    listItems.forEach(item => {
      item.classList[val]('bg-gray-50', 'opacity-50')
    })
  }

  handleDeletedChip(input, value = 'add') {
    if (value === 'delete') {
      const existingChip = input.closest('.new-business-record')?.querySelector('.deleted-chip')
      const existingArrow = input.closest('.new-business-record')?.querySelector('.arrow')

      existingChip.remove()
      existingArrow.remove()
      return
    }

    const chip = this.buildChip()
    const chipContainer = input.closest('.new-business-record')?.querySelector('.chip-container')
    const chipClasses = [
      'bg-gray-400/10',
      'border',
      'border-gray-400',
      'flex',
      'items-center',
      'fade-in',
      'justify-center',
      'deleted-chip',
    ]

    if (!chipContainer) return

    chip.classList.add(...chipClasses)
    chip.innerHTML = 'Deleted'

    chipContainer.appendChild(this.arrowForwardIcon)
    chipContainer.appendChild(chip)
  }

  get arrowForwardIcon() {
    return new Icon('icon-arrow-forward', { classes: 'arrow fade-in mx-1.5 text-gray-400' }).asElement()
  }

  buildChip() {
    const chip = document.createElement('span')
    const classes = ['text-gray-700', 'px-3', 'py-1', 'text-tiny', 'rounded-md', 'font-semibold', 'chip', 'border']

    chip.classList.add(...classes)

    return chip
  }

  hoverMenuHandler() {
    this.$records.forEach(record => {
      const hoverMenu = record.querySelector('.hover-menu')

      record.addEventListener('mouseenter', () => {
        hoverMenu?.classList.remove('hidden')
      })

      record.addEventListener('mouseleave', () => {
        hoverMenu?.classList.add('hidden')
      })
    })
  }

  hideAddLink() {
    if (!this.$addLink) {
      return
    }

    this.$addLink.style.left = '-9999px'
    this.$addLink.style.position = 'absolute'
  }

  disablePopulatedRows() {
    // find the table rows
    const $records = this.$container.querySelectorAll('.new-business-record')

    // loop through each row
    $records.forEach($record => {
      // find the account number for the row
      const accountNumber = $record.querySelector('[data-folios-field="accountNumber"]')?.value

      // if no account number, bail
      if (!accountNumber) {
        return
      }

      // find the associated option in the select
      if (!this.$comboBox) {
        return
      }

      this.disableExistingAccountOptions($record)

      const $remove = $record.querySelector('[data-nested-records-control="remove"]')

      if ($remove) {
        $remove.addEventListener('click', this.reviveAccount.bind(this))
      }

      // look for the account number within the select options
      const accountOption = this.$comboBox.querySelector(`[data-account-number="${accountNumber}"]`)

      // if found, disable the option
      if (accountOption) accountOption.disabled = true
    })
  }

  disableExistingAccountOptions(record) {
    const fieldNames = ['accountName', 'accountNumber', 'repCode']

    fieldNames.forEach(fieldName => {
      const $field = record.querySelector(`[data-folios-field="${fieldName}"]`)

      $field.closest('li').classList.add('pointer-events-none', 'select-none')
      $field.tabIndex = -1

      // We also mark the field as "readonly," but this is only technically
      // effective for text-based inputs (not selects, radio buttons, etc.).
      // Ultimately, its real purpose is to allow us to find the first
      // "interactive" input within the `focusFirstFieldInRow` method.
      $field.setAttribute('readonly', 'readonly')
    })
  }

  handleSelection(tomSelectSelectEvent) {
    const $option = tomSelectSelectEvent.target.selectedOptions[0]
    // const selectedOption = tomSelectSelectEvent.currentTarget

    const record = this.addNewRecord($option) // Add a row to the account table
    this.createSumission($option, record)
    this.clearComboBox() // Reset the select to be empty

    event.preventDefault()
  }

  // Make ajax request to submission endpoint and create submission
  createSumission($option, record) {
    if (!$option) return

    const submissionContainerId = document.querySelector('#submission-container-id').value
    // Which tab is attaching, so the container update can see what it added.
    const containerSessionId = document.querySelector('[data-container-session-id]')?.value
    const { accountName, accountNumber, repCode } = $option.dataset

    /** @type {import('./types/common').SubmissionFormType} */
    const formType = 'NewBusinessSubmission'

    api.submissions
      .create({ accountName, accountNumber, containerSessionId, formType, repCode, submissionContainerId })
      .then(response => response.json)
      .then(data => {
        this.addDataToRecord(data, record)
      })
      .catch()
  }

  deleteSubmission(event) {
    const submissionId = util.findUUIDs(event.detail[0].id)[0]

    api.submissions.destroy({ id: submissionId })
  }

  addDataToRecord(data, record) {
    record.querySelector('[data-review-session-id]').value = data.review_session.id
    record.querySelector('[data-lock-id]').value = data.lock.id
    record.querySelector('[data-submission-id]').value = data.submission.id

    record.id = `record-submission_${data.submission.id}`

    record.dataset.submissionLockIdValue = data.lock.id
    record.dataset.submissionIdValue = data.submission.id
  }

  addNewRecord($option) {
    if ($option) {
      // Get data from selected option's hidden data attributes
      const data = this.getAccountDataFromOption($option)

      // Trigger a click on the Cocoon "Add row" link
      this.$addLink.click()

      const $record = Array.from(this.$container.querySelectorAll('.new-business-record')).at(-1)

      const $remove = $record.querySelector('[data-nested-records-control="remove"]')

      if ($remove) {
        $remove.addEventListener('click', this.reviveAccount.bind(this))
      }

      this.handle401kField(data, $record)
      this.populateRecord($record, data)

      this.$comboBox.tomselect.options[$option.value].disabled = true
      this.$comboBox.tomselect.options[$option.value].$div.ariaDisabled = true
      this.$comboBox.tomselect.options[$option.value].$div.removeAttribute('data-selectable')

      return $record
    }
  }

  getAccountDataFromOption($option) {
    // Grab data attributes from the `<option>` associated with this
    // Select2 event. NOTE: We set these data attributes within the
    // `options_for_folio_select` submission helper method.
    if (!$option) {
      return
    }

    const { accountName, accountNumber, advisorName, repCode } = $option?.dataset

    return { accountName, accountNumber, advisorName, repCode }
  }

  populateRecord($row, data) {
    // Set the fields to populate.
    //
    // NOTE: These array items are derived from the values that we manually set
    // for each field within the `business_submissions/_table_row.html.erb`
    // partial. (Look for instances of `"data-folios-field"` within that file.)
    //
    // IMPORTANT: These values **MUST** match the data attributes within the
    // `options_for_folio_select` submission helper method.
    const fieldNames = Object.keys(data)

    fieldNames.forEach(fieldName => {
      const $field = $row.querySelector(`[data-folios-field="${fieldName}"]`)

      if ($field === null) return

      // Set the field's value
      $field.value = data[fieldName]

      // Make the field readonly by applying classes to the containing `<td>`
      // element; these classes prevent mouse interaction and text selection.
      // We also disable tabbing into the field. These two actions effectively
      // remove mouse/keyboard interaction while keeping the fields enabled.
      // This is important because we want to "disable" interaction, but
      // setting the `disabled` attribute on the field would prevent it from
      // being included in the form submission.

      $field.closest('li').classList.add('pointer-events-none', 'select-none')
      $field.tabIndex = -1

      // We also mark the field as "readonly," but this is only technically
      // effective for text-based inputs (not selects, radio buttons, etc.).
      // Ultimately, its real purpose is to allow us to find the first
      // "interactive" input within the `focusFirstFieldInRow` method.
      $field.setAttribute('readonly', 'readonly')
    })
  }

  reviveAccount(value) {
    const accountNumber =
      value.currentTarget.parentElement.parentElement.children[1].children[0].querySelector('input').value

    if (accountNumber === '') {
      return
    }

    this.$comboBox.tomselect.options[accountNumber].disabled = false

    if (this.$comboBox.tomselect.options[accountNumber].$div) {
      this.$comboBox.tomselect.options[accountNumber].$div.ariaDisabled = false
      this.$comboBox.tomselect.options[accountNumber].$div.setAttribute('data-selectable', true)
    }
  }

  clearComboBox() {
    var value = this.$comboBox.value
    if (value === '') {
      return
    }

    this.$comboBox.tomselect.clear()
  }

  handle401kField(data, row) {
    if (data.accountName) {
      return
    }

    const modelSelectInput = row.querySelector('[data-folios-field="model"]')

    modelSelectInput.add(new Option('401(k)'))
    modelSelectInput.value = '401(k)'
    modelSelectInput.addEventListener('mousedown', event => {
      event.preventDefault()
    })
    modelSelectInput.setAttribute('tabindex', '-1')
    modelSelectInput.classList.add('bg-gray-50', 'cursor-not-allowed')
  }
}

export default NewBusiness
